import type { StepKind } from '../api/types'
import { Icon } from './Icon'

// One visual language for workflow steps everywhere they appear — the builder,
// a drip's journey, the step trail on a contact, the running-workflow dialog:
// a text is a green chat bubble, an email a blue envelope, a task for you a
// purple check, a wait a grey clock. Learn it once, read every screen at a glance.

export type AnyStepKind = StepKind | 'wait'

export const KIND: Record<AnyStepKind, { icon: string; label: string; short: string }> = {
  sms: { icon: 'sms', label: 'Send a text', short: 'Text' },
  email: { icon: 'mail', label: 'Send an email', short: 'Email' },
  task: { icon: 'task_alt', label: 'Task for me', short: 'Your task' },
  wait: { icon: 'schedule', label: 'Wait', short: 'Wait' },
}

export type DotState = 'done' | 'next' | 'upcoming' | 'skipped'

/** A step's icon in its colour. `done` is filled, `next` is ringed, `upcoming`
 *  is a quiet grey outline — so a row of them reads as progress on its own. */
export function KindIcon({ kind, state = 'done', size = 28, title }: {
  kind: AnyStepKind; state?: DotState; size?: number; title?: string
}) {
  return (
    <span className={`kind-dot k-${kind} kd-${state}`} style={{ width: size, height: size }} title={title ?? KIND[kind].short}>
      <Icon name={KIND[kind].icon} size={Math.round(size * 0.58)} />
    </span>
  )
}

/** How far someone is through a workflow: one dot per text, email or task, in
 *  order, with "2 of 5" after it. Paused shows the next dot in amber; stopped
 *  and finished leave the rest grey. */
export function StepTrail({ kinds, done, status }: { kinds: AnyStepKind[]; done: number; status?: string }) {
  const n = Math.min(done, kinds.length)
  const live = status === 'active' || status === 'paused' || status === undefined
  const size = kinds.length > 8 ? 14 : 18
  return (
    <span className={`trail${status === 'paused' ? ' trail-paused' : ''}`} title={`${n} of ${kinds.length} steps done`}>
      <span className="trail-dots">
        {kinds.map((k, i) => (
          <KindIcon key={i} kind={k} size={size}
            state={i < n || status === 'done' ? 'done' : i === n && live ? 'next' : status === 'stopped' ? 'skipped' : 'upcoming'}
            title={`${KIND[k].short} · ${i < n || status === 'done' ? 'done' : i === n && live ? 'next' : 'not yet'}`} />
        ))}
      </span>
      <span className="trail-n">{status === 'done' ? 'Finished' : `${n} of ${kinds.length}`}</span>
    </span>
  )
}
